import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { SenderIdsFormArray } from '../interfaces/forms/sender-ids-form.interface';
import { minArrayLength } from '../validators/min-array-length.validator';
import { SenderIdValidator } from '../validators/sender-id.validator';
import { requiredIfSingleValidator } from '../validators/requiredIfSingle.validator';
import { noDuplicates } from '../interceptors/no-duplicates.interceptor';
import { maxArrayLength } from '../validators/max-array-length.validator';

@Injectable({
  providedIn: 'root',
})
export class SenderIdsFormService {
  public readonly fb = inject(FormBuilder);
  private readonly senderIdValidator = inject(SenderIdValidator);

  public formArray: SenderIdsFormArray;

  private minControlsLength = 1;
  public maxSenderIds = 50;

  getControlSyncValidators(): ValidatorFn[] {
    return [
      (control) => requiredIfSingleValidator(this.formArray, this.minControlsLength)(control),
      Validators.minLength(3),
      Validators.maxLength(11),
    ];
  }

  get controlAsyncValidators() {
    return [
      this.senderIdValidator.get(),
    ];
  }

  public initFormArray(enableMinArrayLengthValidation = true, minControlsLength = 1): SenderIdsFormArray {
    const arrayValidators = [
      ...(enableMinArrayLengthValidation ? [minArrayLength(1)] : []),
      maxArrayLength(this.maxSenderIds),
      noDuplicates(),
    ];

    this.minControlsLength = minControlsLength;

    const controls = [new FormControl('', this.getControlSyncValidators(), this.controlAsyncValidators)];

    this.formArray = this.fb.array<FormControl<string>>(controls, { validators: arrayValidators });

    return this.formArray;
  }

  public addControl() {
    this.formArray.push(new FormControl('', this.getControlSyncValidators(), this.controlAsyncValidators));

    this.formArray.updateValueAndValidity();
    this.formArray.controls.forEach(c => c.updateValueAndValidity());
  }

  public removeControl(index: number) {
    this.formArray.removeAt(index);

    this.formArray.updateValueAndValidity();
    this.formArray.controls.forEach(c => c.updateValueAndValidity());
  }
}
